import { configuration } from 'src/configuration';
import { DataSource } from 'src/types/data-sources';
import { get } from 'src/utils/fetch';
import { to } from 'to-await';

const { accountApiUrl } = configuration;

export type CreateDataSourceDTO = Omit<DataSource, 'dataSourceId'>;
export interface UpdateDataSourceDTO {
  dataSourceId: string;
  name?: string;
  displayName?: string;
  description?: string;
  archive?: boolean;
}

export interface DataSourceWiki {
  id: string;
  metadata?: DataSourceWikiMetadata;
}

interface DataSourceWikiMetadata {
  name: string;
  description: string;
  link?: string;
  provider_name?: string;
  date_range?: DateRange;
  record_count?: number;
  total: number;
  tables: DataSourceWikiTable[];
  demographics: Distribution[];
  highlights: Distribution[];
}

interface DateRange {
  min_date: string;
  max_date: string;
}

interface DataSourceWikiTable {
  name: string;
  description: string;
  record_count: number;
  columns: DataSourceWikiTableColumn[];
}

interface DataSourceWikiTableColumn {
  name: string;
  type: string;
  description: string;
  completeness_rate?: number;
}

export interface Distribution {
  metric: string;
  distribution: Record<string, number>;
  mean_patient_years?: number;
  cumulative_patient_years?: number;
}

const DATA_SOURCE_BASE_URL = `${accountApiUrl}/data-sources`;

export const getDataSources = async ({
  token,
  search,
  after,
}: {
  search: string;
  token: string;
  after: string;
}) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources?search=${search}&limit=15&after=${after}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  if (data?.datasources) {
    return {
      dataSources: data.datasources,
      lastEvaluatedKey: data.lastEvaluatedKey,
    };
  }

  return { dataSources: [], lastEvaluatedKey: null };
};

export const getDataSourceById = async ({
  token,
  dataSourceId,
}: {
  token: string;
  dataSourceId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const createDatasource = async (token: string, createDataSourceDTO: CreateDataSourceDTO) => {
  const response = await fetch(`${accountApiUrl}/data-sources`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({
      ...createDataSourceDTO,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data;
};

export const updateDataSource = async ({
  token,
  dataSourceToUpdate,
}: {
  token: string;
  dataSourceToUpdate: UpdateDataSourceDTO;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceToUpdate.dataSourceId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'PATCH',
    body: JSON.stringify(dataSourceToUpdate),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  const data = await response.json();

  return data.user;
};

export const deleteDataSource = async ({
  dataSourceId,
  token,
}: {
  dataSourceId: string;
  token: string;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }
};

export const getDataSourceOrganizations = async ({
  token,
  dataSourceId,
}: {
  token: string;
  dataSourceId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}/organizations`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const getDataSourceResearchers = async ({
  token,
  dataSourceId,
}: {
  token: string;
  dataSourceId: string;
}) => {
  const response = await fetch(`${accountApiUrl}/data-sources/${dataSourceId}/researchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const getCohortResearchers = async ({
  token,
  dataSourceId,
  cohortId,
}: {
  token: string;
  dataSourceId: string;
  cohortId: string;
}) => {
  const response = await fetch(
    `${accountApiUrl}/data-sources/${dataSourceId}/cohort/${cohortId}/researchers`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message);
  }

  return await response.json();
};

export const getOrganizationUnitsByDataSourceId = async ({
  dataSourceId,
  token,
}: {
  dataSourceId: string;
  token: string;
}) => {
  const [error, response] = await to(
    get(`${DATA_SOURCE_BASE_URL}/${dataSourceId}/organization-units`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
};

export const getOrganizationsByDataSourceId = async ({
  dataSourceId,
  token,
}: {
  dataSourceId: string;
  token: string;
}) => {
  const [error, response] = await to(
    get(`${DATA_SOURCE_BASE_URL}/${dataSourceId}/organizations`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
};

export const getOrganizationUnitsByCohortId = async ({
  dataSourceId,
  cohortId,
  token,
}: {
  dataSourceId: string;
  cohortId: string;
  token: string;
}) => {
  const [error, response] = await to(
    get(`${DATA_SOURCE_BASE_URL}/${dataSourceId}/cohort/${cohortId}/organization-units`, {
      headers: { Authorization: `Bearer ${token}` },
    }),
  );

  if (error) {
    throw new Error(error.message);
  }

  return response;
};
